import { useRef, useState } from "react";
import axios from "axios";
import { Image as ImageIcon, Loader2, Upload, X } from "lucide-react";

import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

const WebsiteImageField = ({
  id,
  label,
  value,
  onChange,
  onUploadStateChange,
  onAssetUploaded,
  required = false,
  validationError = "",
}) => {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [pendingPreview, setPendingPreview] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setError("");
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Choose a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Choose an image smaller than 10 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const reader = new FileReader();
    reader.onload = () => setPendingPreview(reader.result);
    reader.readAsDataURL(file);
    setUploading(true);
    onUploadStateChange?.(true);
    setProgress(0);
    try {
      const response = await axios.post(
        `${API}/storage/upload/direct?image_type=website`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (event) => {
            if (event.total)
              setProgress(Math.round((event.loaded / event.total) * 100));
          },
        },
      );
      setPendingPreview("");
      onAssetUploaded?.(response.data.file_id);
      onChange(response.data.url, response.data.file_id);
    } catch (uploadError) {
      reader.onload = null;
      setPendingPreview("");
      setError(
        uploadError.response?.data?.detail ||
          "The photo could not be uploaded. Your current image was kept.",
      );
    } finally {
      setUploading(false);
      onUploadStateChange?.(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">
        {label}
        {required ? " (required)" : ""}
      </legend>
      <div
        className={`rounded-xl border-2 border-dashed p-3 ${dragging ? "border-[#142e24] bg-[#eef4f1]" : "border-[#cbd5d1]"}`}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          upload(event.dataTransfer.files?.[0]);
        }}
      >
        {pendingPreview || value ? (
          <img
            src={pendingPreview || value}
            alt="Current uploaded preview"
            className="mb-3 aspect-video w-full rounded-lg object-cover"
          />
        ) : (
          <div className="mb-3 flex min-h-28 flex-col items-center justify-center text-center text-sm text-[#60706a]">
            <ImageIcon aria-hidden="true" className="mb-2 h-6 w-6" />
            Drop a photo here, or choose one from your device.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload aria-hidden="true" className="mr-2 h-4 w-4" />
            )}
            {value ? "Replace photo" : "Upload photo"}
          </Button>
          {value && !uploading && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPendingPreview("");
                onChange("", "");
              }}
            >
              <X aria-hidden="true" className="mr-2 h-4 w-4" />
              Remove
            </Button>
          )}
        </div>
        <input
          ref={inputRef}
          id={id}
          aria-label={label}
          className="sr-only"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(event) => upload(event.target.files?.[0])}
        />
        <p className="mt-2 text-xs text-[#60706a]">JPG, PNG, or WebP. Maximum 10 MB.</p>
        {uploading && (
          <p className="mt-2 text-sm" role="status" aria-live="polite">
            Uploading photo: {progress}%
          </p>
        )}
        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        {validationError && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {validationError}
          </p>
        )}
      </div>
      <details>
        <summary className="cursor-pointer text-sm font-medium">Use image URL instead</summary>
        <Input
          className="mt-2"
          type="url"
          value={value || ""}
          onChange={(event) => onChange(event.target.value, "")}
          aria-label={`${label} URL`}
          placeholder="https://"
        />
      </details>
    </fieldset>
  );
};

export default WebsiteImageField;
